import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getSolanaClusterParam } from '@/utils/solanaExplorer';
import {
  Plus,
  Bell,
  CheckCircle,
  AlertCircle,
  Shield,
  Upload,
  FileText,
  Loader2,
  Clock,
  ExternalLink,
  UserCheck,
} from 'lucide-react';
import { FileUploadModal } from './FileUploadModal';
import { AddVaccinationModal } from './AddVaccinationModal';
import { DocumentViewerModal } from './DocumentViewerModal';
import { AssignProfessionalModal } from '../AssignProfessionalModal';
import { Vaccination } from '../../../../api/pets';
import {
  useVaccinations,
  useCreateVaccination,
  useUpdateVaccination,
  petRecordsKeys,
} from '../../../../hooks/usePetRecords';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { parseUploadInitResponse, getAbsoluteUploadUrl } from '@/api/documents';
import { useNotifications } from '../../context/NotificationsContext';
import { usePets } from '../../context/PetsContext';

interface VaccinationRecordsProps {
  petId: string;
}

export function VaccinationRecords({ petId }: VaccinationRecordsProps) {
  const { t } = useTranslation();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [selectedVaccination, setSelectedVaccination] = useState<string | null>(
    null
  );
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [viewingVaccination, setViewingVaccination] =
    useState<Vaccination | null>(null);
  const [showAssignProfessional, setShowAssignProfessional] = useState(false);
  const [verifyingRecordId, setVerifyingRecordId] = useState<string | null>(null);
  const [verificationRequested, setVerificationRequested] = useState<Set<string>>(new Set());

  const {
    data: vaccinations = [],
    isLoading,
    error,
    isFetching,
  } = useVaccinations(petId);
  const queryClient = useQueryClient();
  const createVaccinationMutation = useCreateVaccination(petId);
  const updateVaccinationMutation = useUpdateVaccination(petId);
  const { addNotification } = useNotifications();
  const { pets } = usePets();
  const petName = pets.find((p) => p.id === petId)?.name ?? 'Your pet';

  const handleRequestVerification = (vaccinationId: string) => {
    setVerifyingRecordId(vaccinationId);
    setShowAssignProfessional(true);
  };

  const handleProfessionalAssigned = async (professional: { id: string; name: string; type: string; source: 'existing' | 'invited'; email?: string | null; phone?: string | null }) => {
    if (!verifyingRecordId) return;
    try {
      await apiClient.post(`/pets/${petId}/request-verification`, {
        recordId: verifyingRecordId,
        recordTable: 'vaccinations',
        professionalId: professional.id || undefined,
        professionalName: professional.name,
        professionalEmail: professional.email || undefined,
        professionalType: professional.type || 'veterinary',
      });
      setVerificationRequested((prev) => new Set(prev).add(verifyingRecordId));
      addNotification({
        type: 'document_shared',
        title: 'Verification Requested',
        message: `Verification request sent to ${professional.name}`,
      });
    } catch {
      addNotification({
        type: 'edit_requested',
        title: 'Request Failed',
        message: 'Could not send verification request. Please try again.',
      });
    }
    setShowAssignProfessional(false);
    setVerifyingRecordId(null);
  };

  const getStatusBadge = (vaccination: Vaccination) => {
    const today = new Date();
    const expiry = new Date(vaccination.expiryDate);
    const daysUntilExpiry = Math.floor(
      (expiry.getTime() - today.getTime()) / (24 * 60 * 60 * 1000)
    );

    if (daysUntilExpiry < 0) {
      return (
        <span
          className="flex items-center gap-1 text-red-600 dark:text-red-400 text-sm"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          <AlertCircle className="w-4 h-4" />
          {t('features.healthRecords.expired', 'Expired')}
        </span>
      );
    } else if (daysUntilExpiry <= 30) {
      return (
        <span
          className="flex items-center gap-1 text-yellow-600 dark:text-yellow-400 text-sm"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          <Bell className="w-4 h-4" />
          {t('features.healthRecords.dueSoon', 'Due Soon')}
        </span>
      );
    } else {
      return (
        <span
          className="flex items-center gap-1 text-green-600 dark:text-green-400 text-sm"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          <CheckCircle className="w-4 h-4" />
          {t('features.healthRecords.vaccinationCurrent', 'Current')}
        </span>
      );
    }
  };

  const handleFileUpload = (vaccinationId: string) => {
    setSelectedVaccination(vaccinationId);
    setShowFileUpload(true);
  };

  const handleDocumentView = (vaccination: Vaccination) => {
    setViewingVaccination(vaccination);
    setShowDocumentViewer(true);
  };

  const handleAddVaccination = async (vaccinationData: {
    vaccine: string;
    dateAdministered: string;
    expiryDate: string;
    boosterDate?: string;
    administeredBy: string;
    clinicName: string;
    batchNumber: string;
    notes?: string;
  }) => {
    try {
      await createVaccinationMutation.mutateAsync({
        vaccineName: vaccinationData.vaccine,
        dateAdministered: vaccinationData.dateAdministered,
        expiryDate: vaccinationData.expiryDate,
        boosterDate: vaccinationData.boosterDate,
        administeredBy: vaccinationData.administeredBy,
        clinicName: vaccinationData.clinicName,
        batchNumber: vaccinationData.batchNumber,
        verified: false,
        notes: vaccinationData.notes,
      });
      addNotification({
        type: 'record_added',
        title: 'Health record added',
        message: `Vaccination "${vaccinationData.vaccine}" was added for ${petName}.`,
        petId,
        petName,
        icon: '💉',
        source: 'E-Passport',
      });
      setShowAddModal(false);
    } catch (err) {
      console.error('Failed to add vaccination:', err);
      alert(
        t(
          'features.healthRecords.errors.addVaccinationFailed',
          'Failed to add vaccination. Please try again.'
        )
      );
    }
  };

  // Format transaction signature for display
  const formatTxSignature = (sig?: string) => {
    if (!sig) return null;
    return sig.substring(0, 8) + '...' + sig.substring(sig.length - 4);
  };

  // Only show loading spinner on initial load (no cached data)
  if (isLoading && vaccinations.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
        <span className="ml-3 text-gray-600 dark:text-white/70">
          {t(
            'features.healthRecords.loadingVaccinationRecords',
            'Loading vaccination records...'
          )}
        </span>
      </div>
    );
  }

  if (error && vaccinations.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 dark:text-red-400 mb-4">
          {t(
            'features.healthRecords.failedToLoadVaccinations',
            'Failed to load vaccinations'
          )}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          {t('features.healthRecords.tryAgain')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3
            className="text-xl mb-1 text-gray-900 dark:text-white"
            style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
          >
            {t('features.healthRecords.vaccinations')}
          </h3>
          <p
            className="text-sm text-gray-600 dark:text-white/70"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('features.healthRecords.vaccinationsDesc')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isFetching && (
            <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
          )}
          <button
            onClick={() => setShowAddModal(true)}
            disabled={createVaccinationMutation.isPending}
            className="flex items-center gap-2 bg-blue-600 dark:bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors disabled:opacity-50"
            style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
          >
            {createVaccinationMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            {t('features.healthRecords.addVaccination')}
          </button>
        </div>
      </div>

      {/* Empty state */}
      {vaccinations.length === 0 && (
        <div className="text-center py-12 bg-gray-50 dark:bg-[#0F0E16] rounded-lg border border-gray-200 dark:border-white/8">
          <p
            className="text-gray-500 dark:text-white/50 mb-4"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('features.healthRecords.noVaccinationYet')}
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="text-blue-600 dark:text-blue-400 hover:underline"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('features.healthRecords.addFirstVaccination')}
          </button>
        </div>
      )}

      {vaccinations.length > 0 && (
        <>
          {/* Upcoming Reminders */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-500/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Bell className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              <h4
                className="text-gray-900 dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
              >
                {t(
                  'features.healthRecords.upcomingBoosters',
                  'Upcoming Boosters'
                )}
              </h4>
            </div>
            <div className="space-y-2">
              {vaccinations
                .filter((v) => {
                  if (!v.boosterDate) return false;
                  const today = new Date();
                  const booster = new Date(v.boosterDate);
                  const daysUntilBooster = Math.floor(
                    (booster.getTime() - today.getTime()) /
                      (24 * 60 * 60 * 1000)
                  );
                  return daysUntilBooster >= 0 && daysUntilBooster <= 60;
                })
                .map((v) => (
                  <div
                    key={v.id}
                    className="flex items-center justify-between text-sm"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    <span className="text-gray-900 dark:text-white">
                      {v.vaccineName}
                    </span>
                    <span className="text-yellow-700 dark:text-yellow-300">
                      {t(
                        'features.healthRecords.boosterDueDate',
                        'Due: {{date}}',
                        { date: new Date(v.boosterDate!).toLocaleDateString() }
                      )}
                    </span>
                  </div>
                ))}
              {vaccinations.filter((v) => {
                if (!v.boosterDate) return false;
                const today = new Date();
                const booster = new Date(v.boosterDate);
                const daysUntilBooster = Math.floor(
                  (booster.getTime() - today.getTime()) / (24 * 60 * 60 * 1000)
                );
                return daysUntilBooster >= 0 && daysUntilBooster <= 60;
              }).length === 0 && (
                <p
                  className="text-sm text-gray-500 dark:text-white/50"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t(
                    'features.healthRecords.noUpcomingBoosters',
                    'No upcoming boosters in the next 60 days'
                  )}
                </p>
              )}
            </div>
          </div>

          {/* Vaccination List */}
          <div className="space-y-4">
            {vaccinations.map((vaccination) => (
              <div
                key={vaccination.id}
                className="bg-white dark:bg-[#0F0E16] border border-gray-200 dark:border-white/8 rounded-lg p-6"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h4
                      className="mb-2 text-gray-900 dark:text-white"
                      style={{
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 600,
                      }}
                    >
                      {vaccination.vaccineName}
                    </h4>
                    <div
                      className="space-y-1 text-sm text-gray-600 dark:text-white/70"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      <div>
                        {t(
                          'features.healthRecords.administeredDate',
                          'Administered: {{date}}',
                          {
                            date: new Date(
                              vaccination.dateAdministered
                            ).toLocaleDateString(),
                          }
                        )}
                      </div>
                      <div>
                        {t(
                          'features.healthRecords.administeredByClinic',
                          'By: {{name}} - {{clinic}}',
                          {
                            name: vaccination.administeredBy,
                            clinic: vaccination.clinicName,
                          }
                        )}
                      </div>
                      <div>
                        {t(
                          'features.healthRecords.batchLabel',
                          'Batch: {{number}}',
                          { number: vaccination.batchNumber }
                        )}
                      </div>
                      <div>
                        {t(
                          'features.healthRecords.expiresDate',
                          'Expires: {{date}}',
                          {
                            date: new Date(
                              vaccination.expiryDate
                            ).toLocaleDateString(),
                          }
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {getStatusBadge(vaccination)}
                    {vaccination.verified && (
                      <span
                        className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        <Shield className="w-3 h-3" />
                        {t('features.healthRecords.verifiedBadge')}
                      </span>
                    )}
                    {vaccination.credentialIssued && (
                      <span
                        className="flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        <CheckCircle className="w-3 h-3" />
                        {t(
                          'features.healthRecords.onChainCertified',
                          'On-Chain Certified'
                        )}
                      </span>
                    )}
                  </div>
                </div>

                {vaccination.txSignature && (
                  <div className="bg-gray-50 dark:bg-white/5 rounded p-3 mb-3">
                    <div
                      className="text-xs text-gray-500 dark:text-white/50 mb-1"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t(
                        'features.healthRecords.transactionSignatureSolana',
                        'Transaction Signature (Solana)'
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="font-mono text-xs text-gray-700 dark:text-white/80">
                        {formatTxSignature(vaccination.txSignature)}
                      </div>
                      <a
                        href={`https://explorer.solana.com/tx/${vaccination.txSignature}${getSolanaClusterParam()}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400 hover:underline"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {t(
                          'features.healthRecords.viewOnSolana',
                          'View on Solana'
                        )}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                )}


                {vaccination.notes && (
                  <div
                    className="text-sm text-gray-600 dark:text-white/70 mb-3"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    <span className="text-gray-500 dark:text-white/50">
                      {t('features.healthRecords.notesLabel', 'Notes')}:
                    </span>{' '}
                    {vaccination.notes}
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={() => handleFileUpload(vaccination.id)}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 dark:border-white/20 rounded hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-gray-700 dark:text-white/90"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    <Upload className="w-4 h-4" />
                    {t(
                      'features.healthRecords.attachCertificate',
                      'Attach Certificate'
                    )}
                  </button>
                  <button
                    onClick={() => handleDocumentView(vaccination)}
                    disabled={!vaccination.documentHash}
                    className={`flex items-center gap-2 px-3 py-1.5 text-sm border rounded transition-colors ${
                      vaccination.documentHash
                        ? 'border-gray-300 dark:border-white/20 hover:bg-gray-50 dark:hover:bg-white/5 text-gray-700 dark:text-white/90'
                        : 'border-gray-200 dark:border-white/10 text-gray-400 dark:text-white/30 cursor-not-allowed'
                    }`}
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    <FileText className="w-4 h-4" />
                    {t('features.healthRecords.viewDocument')}
                  </button>
                  {!vaccination.verified && !verificationRequested.has(vaccination.id) && (
                    <button
                      onClick={() => handleRequestVerification(vaccination.id)}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm border-2 border-green-500 dark:border-green-400 text-green-600 dark:text-green-400 rounded hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      <UserCheck className="w-4 h-4" />
                      Request Verification
                    </button>
                  )}
                  {!vaccination.verified && verificationRequested.has(vaccination.id) && (
                    <span
                      className="flex items-center gap-2 px-3 py-1.5 text-sm border border-yellow-400 dark:border-yellow-500/50 text-yellow-600 dark:text-yellow-400 rounded bg-yellow-50 dark:bg-yellow-900/10"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      <Clock className="w-4 h-4" />
                      Verification Pending
                    </span>
                  )}
                  {!vaccination.credentialIssued && vaccination.verified && (
                    <button
                      className="flex items-center gap-2 px-3 py-1.5 text-sm bg-purple-600 dark:bg-purple-700 text-white rounded hover:bg-purple-700 dark:hover:bg-purple-600 transition-colors"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      <Shield className="w-4 h-4" />
                      {t(
                        'features.healthRecords.issueCredential',
                        'Issue Credential'
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Smart Contract Audit Trail */}
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border border-purple-200 dark:border-purple-500/30 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              <h4
                className="text-gray-900 dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
              >
                {t(
                  'features.healthRecords.smartContractAuditTrail',
                  'Smart Contract Audit Trail'
                )}
              </h4>
            </div>
            <p
              className="text-sm text-gray-700 dark:text-purple-100 mb-4"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {t(
                'features.healthRecords.auditTrailDesc',
                'When veterinarians issue credentials, a cryptographic proof is stored on Solana blockchain, making the vaccination record independently verifiable by any third party.'
              )}
            </p>
            <div className="space-y-2">
              {vaccinations.slice(0, 5).map((vaccination) => (
                <div
                  key={vaccination.id}
                  className="flex items-center gap-3 text-sm bg-white/50 dark:bg-white/5 rounded p-2"
                >
                  <Clock className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  <span
                    className="text-gray-600 dark:text-white/70"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {new Date(vaccination.createdAt).toLocaleString()}
                  </span>
                  <span
                    className="flex-1 text-gray-700 dark:text-white/80"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t(
                      'features.healthRecords.vaccinationRecorded',
                      '{{vaccineName}} vaccination recorded',
                      { vaccineName: vaccination.vaccineName }
                    )}
                  </span>
                  {vaccination.txSignature ? (
                    <a
                      href={`https://explorer.solana.com/tx/${vaccination.txSignature}${getSolanaClusterParam()}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400 hover:underline"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      TX: {formatTxSignature(vaccination.txSignature)}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <span
                      className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-1 rounded"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      <Clock className="w-3 h-3" />
                      {t('features.healthRecords.awaitingBlockchainSync')}
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div
              className="text-xs text-gray-600 dark:text-white/70 mt-4"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {t(
                'features.healthRecords.onChainCredentialCount',
                '{{count}} of {{total}} vaccinations have on-chain credentials',
                {
                  count: vaccinations.filter((v) => v.credentialIssued).length,
                  total: vaccinations.length,
                }
              )}
            </div>
          </div>
        </>
      )}

      {/* Info Note */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-500/30 rounded-lg p-4">
        <h4
          className="mb-2 text-gray-900 dark:text-white"
          style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
        >
          {t('features.healthRecords.whyVaccinationsMatter', {
            defaultValue: 'Why vaccination records matter',
          })}
        </h4>
        <p
          className="text-sm text-gray-700 dark:text-blue-100"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          {t('features.healthRecords.whyVaccinationsBody', {
            defaultValue:
              'Keeping vaccine history up to date helps prevent missed boosters and gives vets trusted records for travel and treatment decisions.',
          })}
        </p>
      </div>

      {showFileUpload && selectedVaccination && (
        <FileUploadModal
          onClose={() => {
            setShowFileUpload(false);
            setSelectedVaccination(null);
          }}
          category="Vaccine Certificate"
          onUpload={async (fileData) => {
            if (!selectedVaccination) return;
            const vaccinationId = selectedVaccination;
            const contentType =
              fileData.file.type || 'application/octet-stream';
            const initRes = await apiClient.post('/documents/upload-init', {
              filename: fileData.file.name,
              contentType,
              folder: 'vaccination-certificates',
              ttlSeconds: 900,
            });
            const raw = initRes.data as
              | {
                  uploadUrl?: string;
                  url?: string;
                  key?: string;
                  body?: unknown;
                  data?: {
                    uploadUrl?: string;
                    url?: string;
                    key?: string;
                  };
                }
              | undefined;
            const body =
              typeof raw?.body === 'string'
                ? (() => {
                    try {
                      return JSON.parse(raw.body) as Record<string, unknown>;
                    } catch {
                      return null;
                    }
                  })()
                : raw?.body && typeof raw.body === 'object'
                  ? (raw.body as Record<string, unknown>)
                  : null;
            const nested =
              raw?.data && typeof raw.data === 'object' ? raw.data : null;
            const uploadUrl =
              (typeof raw?.uploadUrl === 'string' && raw.uploadUrl) ||
              (typeof raw?.url === 'string' && raw.url) ||
              (typeof nested?.uploadUrl === 'string' && nested.uploadUrl) ||
              (typeof nested?.url === 'string' && nested.url) ||
              (body && typeof body.uploadUrl === 'string'
                ? body.uploadUrl
                : '') ||
              (body && typeof body.url === 'string' ? body.url : '');
            const key =
              (typeof raw?.key === 'string' && raw.key) ||
              (typeof nested?.key === 'string' && nested.key) ||
              (body && typeof body.key === 'string' ? body.key : '');

            if (!uploadUrl || !key) {
              throw new Error('Upload init did not return a valid upload URL');
            }

            if (/^https?:\/\//i.test(uploadUrl)) {
              const putRes = await fetch(uploadUrl, {
                method: 'PUT',
                body: fileData.file,
              });
              if (!putRes.ok) {
                throw new Error(`Upload failed with status ${putRes.status}`);
              }
            } else {
              const relativeUrl = uploadUrl.startsWith('/')
                ? uploadUrl
                : `/${uploadUrl}`;
              try {
                await apiClient.put(relativeUrl, fileData.file, {
                  headers: { 'Content-Type': contentType },
                });
              } catch (err) {
                const status = (err as { response?: { status?: number } })
                  ?.response?.status;
                if (status !== 405) throw err;
                await apiClient.post(relativeUrl, fileData.file, {
                  headers: { 'Content-Type': contentType },
                });
              }
            }
            await updateVaccinationMutation.mutateAsync({
              vaccinationId: selectedVaccination,
              data: { documentHash: key },
            });
          }}
        />
      )}

      {showAddModal && (
        <AddVaccinationModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddVaccination}
        />
      )}

      {showDocumentViewer && viewingVaccination?.documentHash && (
        <DocumentViewerModal
          onClose={() => {
            setShowDocumentViewer(false);
            setViewingVaccination(null);
          }}
          documentHash={viewingVaccination.documentHash}
          documentTitle={viewingVaccination.vaccineName}
        />
      )}

      {showAssignProfessional && (
        <AssignProfessionalModal
          isOpen={showAssignProfessional}
          onClose={() => {
            setShowAssignProfessional(false);
            setVerifyingRecordId(null);
          }}
          professionalType="veterinary"
          professionalLabel="Veterinarian"
          petId={petId}
          onAssign={handleProfessionalAssigned}
        />
      )}
    </div>
  );
}
